import { createContext } from "react";

export type DescriptionListLayout =
  | "stacked"
  | "horizontal"
  | "justified"
  | "grid";
export type DescriptionListColumns = 1 | 2 | 3 | 4;
export type DescriptionListDensity = "comfortable" | "compact";

interface LayoutClasses {
  /** The `<dl>`, before columns, density and dividers. */
  readonly list: string;
  /** Item columns on the `<dl>`: one column below `sm`, `columns` from `sm` up. */
  readonly columns: Readonly<Record<DescriptionListColumns, string>>;
  /** One `div > dt + dd` group. */
  readonly item: string;
  /** Added to a `fullWidth` group: it spans every column. */
  readonly fullWidth: string;
  readonly term: string;
  readonly details: string;
}

/* The label and value classes. Every layout shares them, so a label reads the
   same wherever it sits. */
const TERM =
  "text-dense-2 uppercase tracking-wider text-pb-text-secondary font-semibold";
const DETAILS =
  "min-w-0 break-words text-xs text-pb-text [overflow-wrap:anywhere]";

const ITEM_COLUMNS: LayoutClasses["columns"] = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-4",
};

/* One entry per layout (registry, not a switch). Literal class strings so
   Tailwind sees them. */
export const DESCRIPTION_LIST_LAYOUT: Readonly<
  Record<DescriptionListLayout, LayoutClasses>
> = {
  stacked: {
    list: "grid gap-x-6",
    columns: ITEM_COLUMNS,
    item: "flex min-w-0 flex-col gap-1",
    fullWidth: "col-span-full",
    term: TERM,
    details: DETAILS,
  },
  horizontal: {
    list: "grid gap-x-6",
    columns: ITEM_COLUMNS,
    item: "flex min-w-0 flex-wrap items-center gap-2",
    fullWidth: "col-span-full",
    term: TERM,
    details: DETAILS,
  },
  justified: {
    list: "grid gap-x-6",
    columns: ITEM_COLUMNS,
    item: "flex min-w-0 items-baseline justify-between gap-3",
    fullWidth: "col-span-full",
    term: TERM,
    details: `${DETAILS} text-end`,
  },
  /* Each group is a subgrid over one auto/1fr pair of tracks, so every label
     and every value lines up without `display: contents` (which would drop
     the group's box and with it the divider). */
  grid: {
    list: "grid gap-x-4",
    columns: {
      1: "grid-cols-[auto_minmax(0,1fr)]",
      2: "grid-cols-[auto_minmax(0,1fr)] sm:grid-cols-[repeat(2,auto_minmax(0,1fr))]",
      3: "grid-cols-[auto_minmax(0,1fr)] sm:grid-cols-[repeat(3,auto_minmax(0,1fr))]",
      4: "grid-cols-[auto_minmax(0,1fr)] sm:grid-cols-[repeat(4,auto_minmax(0,1fr))]",
    },
    item: "col-span-2 grid min-w-0 grid-cols-subgrid items-baseline",
    fullWidth: "col-span-full [&>dd]:col-[2/-1]",
    term: TERM,
    details: DETAILS,
  },
};

/* Density is the spacing between rows, nothing else. Without dividers it is
   the row gap; with dividers it moves into each group's block padding, so the
   hairline sits midway between two rows (the List precedent). */
export const DESCRIPTION_LIST_DENSITY: Readonly<
  Record<
    DescriptionListDensity,
    { readonly gap: string; readonly divided: string }
  >
> = {
  comfortable: { gap: "gap-y-3", divided: "[&>*]:py-2.5" },
  compact: { gap: "gap-y-1.5", divided: "[&>*]:py-1.5" },
};

/* A hairline above every group after the first; from `sm` up the first row
   holds `columns` groups, so those lose it. A `fullWidth` group inside the
   first row breaks that count: place wide groups after it when divided. */
export const DESCRIPTION_LIST_DIVIDED =
  "[&>*]:border-pb-border [&>*+*]:border-t";
export const DESCRIPTION_LIST_FIRST_ROW: Readonly<
  Record<DescriptionListColumns, string>
> = {
  1: "",
  2: "sm:[&>*:nth-child(-n+2)]:border-t-0",
  3: "sm:[&>*:nth-child(-n+3)]:border-t-0",
  4: "sm:[&>*:nth-child(-n+4)]:border-t-0",
};

/** The enclosing list's layout, read by each `DescriptionItem`. */
export const DescriptionListLayoutContext =
  createContext<DescriptionListLayout>("stacked");
